import { axiosInstance } from '@/api/axiosInstance';
import type { UserReferenceData } from '@/entities/applications/Application';
import type { ScanStatus } from '@/entities/applications/scan';
import type { PaginatedResponse } from '@/types';

import type { MockJobEventLevel, MockJobKind, MockJobStatus } from './enums';

const jobBase = '/mock_api/v1/mock_job/';

export type MockJobEvent = {
  at: string;
  level: MockJobEventLevel;
  message: string;
  endpoint?: string;
  system?: string;
};

export type MockJobSummary = {
  system?: string;
  endpoints?: number;
  skipped?: string[];
  warnings?: number;
  enriched?: number;
  enrich_kept?: number;
};

export type MockJobOptions = {
  name?: string;
  slug?: string;
  replace?: boolean;
  enrich?: boolean;
  app?: string;
};

export type MockJobData = {
  uuid: string;
  kind: MockJobKind;
  status: MockJobStatus;
  system: { uuid: string; name: string; slug: string } | null;
  created_by: UserReferenceData | null;
  scan_status: ScanStatus;
  options: MockJobOptions;
  total: number;
  done: number;
  percent: number;
  events: MockJobEvent[];
  summary: MockJobSummary | null;
  error: string;
  started_at: string | null;
  finished_at: string | null;
  created: string;
  modified: string;
};

export const isMockJobFinished = (job: Pick<MockJobData, 'status'>) =>
  job.status === 'succeeded' || job.status === 'failed';

export const fetchMockJob = async (
  uuid: string,
  { signal }: { signal?: AbortSignal } = {},
) => {
  const response = await axiosInstance.get<MockJobData>(`${jobBase}${uuid}/`, {
    signal,
  });
  return response.data;
};

export const fetchLatestMockJob = async (
  system: string,
  { signal }: { signal?: AbortSignal } = {},
) => {
  const response = await axiosInstance.get<PaginatedResponse<MockJobData>>(
    jobBase,
    { params: { system, ordering: '-created', page_size: '1' }, signal },
  );
  return response.data.results[0] ?? null;
};

export const createMockImportJob = async (
  source: number | string,
  options: MockJobOptions,
) => {
  const response = await axiosInstance.post<MockJobData>(jobBase, {
    source: { id: source },
    options,
  });
  return response.data;
};
